import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { apiService } from '../services/api';
import { useToast } from '../context/ToastContext';
import './ChatPanel.css';

const ChatPanel = ({ analysisId, hasDataset, onOpenChange }) => {
  const { toast } = useToast();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const messagesEndRef = useRef(null);

  const canChat = Boolean(analysisId) && hasDataset;

  useEffect(() => {
    if (!canChat || !isOpen) {
      if (!canChat) {
        setMessages([]);
      }
      return;
    }

    const fetchMessages = async () => {
      try {
        setInitialLoading(true);
        setError(null);
        const response = await apiService.getAnalysisChat(analysisId);
        setMessages(response.data?.messages || []);
      } catch (err) {
        // Soft-fail, user can still try sending a first message
        setError('Unable to load previous chat. You can still ask a new question.');
      } finally {
        setInitialLoading(false);
      }
    };

    fetchMessages();
  }, [analysisId, canChat, isOpen]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, loading]);

  const handleSend = async (overrideText) => {
    const text = (typeof overrideText === 'string' ? overrideText : input).trim();
    if (!text || !canChat || loading) return;
    setInput('');
    setError(null);

    const optimisticUserMessage = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content: text,
      created_at: new Date().toISOString(),
      optimistic: true,
    };
    setMessages((prev) => [...prev, optimisticUserMessage]);
    setLoading(true);

    try {
      const response = await apiService.sendAnalysisChat(analysisId, text);
      const { user_message, assistant_message } = response.data || {};

      setMessages((prev) =>
        [
          ...prev.filter((m) => !m.optimistic),
          user_message,
          assistant_message,
        ].filter(Boolean)
      );
    } catch (err) {
      console.error('Error sending chat message', err);
      setMessages((prev) => prev.filter((m) => !m.optimistic));
      if (err.response?.status === 403) {
        const msg = err.response?.data?.detail || 'You have reached your chat message limit.';
        toast(msg, 'error');
      } else {
        setError('Unable to send message. Please try again.');
        setInput(text);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const toggleOpen = () => {
    setIsOpen((prev) => !prev);
  };

  useEffect(() => {
    if (onOpenChange) {
      onOpenChange(isOpen);
    }
  }, [isOpen, onOpenChange]);

  const exampleQuestions = [
    "Which features look most predictive of my target?",
    "Are there any data quality issues I should fix first?",
    "What are the best next steps using my predictions?"
  ];

  const handleExampleClick = (question) => {
    if (!canChat || loading) return;
    handleSend(question);
  };

  const renderBodyContent = () => {
    if (!canChat) {
      return (
        <div className="analysis-chat__empty">
          Upload a dataset and start an analysis to chat about your data.
        </div>
      );
    }

    if (!messages.length && !initialLoading) {
      return null; // Show nothing, examples will be shown in footer
    }

    return (
      <>
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`analysis-chat__message analysis-chat__message--${msg.role} q-md`}
          >
            <ReactMarkdown>{msg.content}</ReactMarkdown>
            <span className="analysis-chat__timestamp">
              {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
        ))}
        {loading && (
          <div className="analysis-chat__typing">
            Qombra is thinking…
          </div>
        )}
        <div ref={messagesEndRef} />
      </>
    );
  };

  return (
    <div className={`analysis-chat-drawer ${isOpen ? 'is-open' : ''}`}>
      <button
        type="button"
        className={`analysis-chat-drawer__toggle ${isOpen ? 'is-open' : ''}`}
        onClick={toggleOpen}
        aria-label={isOpen ? 'Hide analysis assistant' : 'Show analysis assistant'}
      >
        Chat
        <span className="analysis-chat-drawer__toggle-icon" aria-hidden>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-chevron-compact-left" viewBox="0 0 16 16">
            <path fillRule="evenodd" d="M9.224 1.553a.5.5 0 0 1 .223.67L6.56 8l2.888 5.776a.5.5 0 1 1-.894.448l-3-6a.5.5 0 0 1 0-.448l3-6a.5.5 0 0 1 .67-.223"/>
          </svg>
        </span>
      </button>

      <aside
        className={`analysis-chat analysis-chat--drawer ${isOpen ? 'is-open' : ''}`}
        aria-hidden={!isOpen}
      >
        <div className="analysis-chat__header">
          <div className="analysis-chat__header-text">
            <h2 className="analysis-chat__title">Ask about this analysis</h2>
            <p className="analysis-chat__subtitle">
              Understand your data & insights better and define next steps.
            </p>
          </div>
        </div>

        <div className="analysis-chat__body">
          {renderBodyContent()}

          {!messages.length && canChat && (
            <div className="analysis-chat__examples">
              {exampleQuestions.map((question, index) => (
                <button
                  key={index}
                  type="button"
                  className="analysis-chat__example-btn"
                  onClick={() => handleExampleClick(question)}
                  disabled={loading}
                >
                  {question}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="analysis-chat__footer">
          <div className="analysis-chat__input-wrapper">
            <textarea
              className="analysis-chat__textarea"
              placeholder={
                canChat
                  ? 'Ask a question about your data or model…'
                  : 'Upload data to start chatting about your analysis…'
              }
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={!canChat || loading}
              rows={1}
            />
            <button
              type="button"
              className="analysis-chat__send-btn"
              onClick={handleSend}
              disabled={!canChat || loading || !input.trim()}
              aria-label="Send message"
            >
              {loading ? (
                <div className="analysis-chat__loading">
                  <span className="analysis-chat__dot"></span>
                  <span className="analysis-chat__dot"></span>
                  <span className="analysis-chat__dot"></span>
                </div>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-send" viewBox="0 0 16 16">
                  <path d="M15.854.146a.5.5 0 0 1 .11.54l-5.819 14.547a.75.75 0 0 1-1.329.124l-3.178-4.995L.643 7.184a.75.75 0 0 1 .124-1.33L15.314.037a.5.5 0 0 1 .54.11ZM6.636 10.07l2.761 4.338L14.13 2.576zm6.787-8.201L1.591 6.602l4.339 2.76z"/>
                </svg>
              )}
            </button>
          </div>
          {error && <div className="analysis-chat__error">{error}</div>}
        </div>
      </aside>
    </div>
  );
};

export default ChatPanel;
